// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type FC,
	type PropsWithChildren,
	type ReactNode,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { getDocument } from '@atlaskit/browser-apis';

import { cssMap } from '@atlaskit/css';

import { Box } from '@atlaskit/primitives/compiled';

import {
	useElement,
	useInlineNearElement,
	useMountToHtmlElement,
} from '@post-office/components--post-office-team';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import type {
	IssuePreviewPanelSpotlightInlineRenderProps,
	IssuePreviewPanelSpotlightSidebarRenderProps,
	BaseRenderProps,
} from '@post-office/browser--jira-empanada/issue-navigator-placement';

import { PopoverContent, type PopoverContentProps } from '@atlaskit/spotlight/popover-content';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import glanceNavArrowsIllustration from '../assets/glance_nav_arrows_illustration.png';

import { TourCard, type TourCardProps } from './TourCard';

import { tourMessages } from './tourMessages';

import { deriveTourPlan, type ViewSurface } from './tourSequence';

/**
 * The open-work-item control in the first table row. Only rendered while its row is highlighted, so
 * the intro step has to drive the highlight before it can anchor.
 */
const OPEN_ISSUE_LINK_SELECTOR =
	'[data-testid="native-issue-table.common.ui.issue-cells.issue-summary.open-issue-link"]';

/**
 * The 1x1 anchors Jira renders beside the navigation arrows, one per surface. Selected per surface
 * rather than by the id both also carry, so a stale node from a closed surface cannot satisfy the
 * lookup.
 */
const PANEL_ARROWS_ANCHOR_SELECTOR =
	'[data-testid="platform-issue-preview-panel.arrows-changeboarding-target"]';

const MODAL_ARROWS_ANCHOR_SELECTOR =
	'[data-testid="issue-navigator.ui.issue-preview-panel.arrows-changeboarding-target"]';

/**
 * The resize handle at the preview panel's leading edge, set in Jira's `PreviewPanel.tsx`. If that
 * testId changes the resize step silently renders nothing rather than erroring.
 */
const RESIZE_HANDLE_SELECTOR = '[data-testid="preview-panels.panel-splitter"]';

/** ADS offset as [cross-axis shift, gap]. The gap clears the panel's own edge. */
const RESIZE_CARD_GAP = 8;

const styles = cssMap({
	/**
	 * ADS aims the caret at the anchor's vertical centre, so an empty target collapses to a point at
	 * the handle's top edge. The height has to be in flow — `position: absolute` collapses it again —
	 * hence measured rather than expressed in CSS. Kept out of the hit area so it cannot swallow drags.
	 */
	resizeAnchor: {
		pointerEvents: 'none',
		width: '1px',
	},
});

/** Card props the steps supply themselves, rather than receiving from the controller. */
type StepContentProps = Omit<TourCardProps, 'body' | 'heading' | 'image'>;

/**
 * A counter that increments whenever the element matching `selectors` is replaced, for use as a
 * step's remount key.
 *
 * `useElement` stops watching after the first mutation whose selector matches anything, so a surface
 * that churns while it loads burns its single recovery and leaves the step portalled into a detached
 * node. Lives in the controller, not the step: a hook the remount resets cannot be what triggers it.
 */
const useAnchorGeneration = (selectors: string): number => {
	const [generation, setGeneration] = useState(0);
	const anchorRef = useRef<Element | null>(null);

	useEffect(() => {
		const doc = getDocument();
		const root = doc?.body;

		// A new selector starts from nothing, or its first resolve reads as a swap.
		anchorRef.current = null;

		if (!doc || !root) {
			return;
		}

		const sync = () => {
			const found = doc.querySelector(selectors);

			if (found === anchorRef.current) {
				return;
			}

			// The first resolve is the step's own lookup finding its anchor, not a replacement.
			const hadAnchor = anchorRef.current !== null;
			anchorRef.current = found;

			if (hadAnchor) {
				setGeneration((current) => current + 1);
			}
		};

		sync();

		const observer = new MutationObserver(sync);

		observer.observe(root, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, [selectors]);

	return generation;
};

/**
 * Mounts children into the first row's open-work-item control. Only the popover target goes in
 * there, or the card is trapped in the merged cell's stacking context.
 */
const RenderToOpenIssueLink: FC<PropsWithChildren> = ({ children }) => {
	const renderInline = useMountToHtmlElement({
		selectors: OPEN_ISSUE_LINK_SELECTOR,
		as: 'inline',
		position: 'inside',
	});

	return renderInline(children);
};

type RemoteCardStepProps = TourCardProps &
	Pick<PopoverContentProps, 'offset' | 'placement'> & {
		/**
		 * Puts the popover target inside the step's anchor. A function, not a component: the hooks that
		 * supply it return a new one every render, which as a component type would remount the target.
		 */
		renderTarget: (target: ReactNode) => ReactNode;
		isVisible: boolean;
		/** Supply one only when the card's placement needs the target to have a box. */
		targetChild?: ReactNode;
	};

/**
 * A step whose target is portalled into its anchor while the card stays in the normal React tree,
 * for anchors sitting inside a container that would trap the card — the table's merged cell and the
 * panel splitter both do.
 *
 * Click-outside dismissal is off, here and on every step. ADS defaults it on, which made opening a
 * work item from the anchored row dismiss the tour — and `handleDismiss` reports a click, which
 * `click-check-filter` treats as final, so the tour would burn itself on first use. Dismissal is
 * left to the X and Escape.
 */
const RemoteCardStep: FC<RemoteCardStepProps> = ({
	isVisible,
	offset,
	placement,
	renderTarget,
	targetChild = null,
	...cardProps
}) => (
	<PopoverProvider>
		{renderTarget(<PopoverTarget>{targetChild}</PopoverTarget>)}
		<PopoverContent
			dismiss={cardProps.onDismiss}
			isVisible={isVisible}
			offset={offset}
			placement={placement}
			shouldDismissOnClickOutside={false}
		>
			<TourCard {...cardProps} />
		</PopoverContent>
	</PopoverProvider>
);

type IntroStepProps = StepContentProps &
	Pick<BaseRenderProps, 'openWorkItem'> &
	Pick<IssuePreviewPanelSpotlightInlineRenderProps, 'setIsActive' | 'state'>;

/**
 * "New ways to view work in context", anchored to the first row.
 *
 * Next asks Jira to open the work item before advancing, so the arrows step has a surface to anchor
 * in and the product keeps both the choice of surface and its own instrumentation on the selection.
 *
 * The row has to be highlighted before there is anything to anchor to, so this asks the placement to
 * highlight it and renders nothing until Jira reports back that it has.
 */
const IntroStep: FC<IntroStepProps> = ({
	onNext,
	openWorkItem,
	setIsActive,
	state,
	...cardProps
}) => {
	const hasRequestedHighlightRef = useRef(false);

	const handleNext = useCallback(() => {
		openWorkItem?.();
		onNext();
	}, [onNext, openWorkItem]);

	useEffect(() => {
		if (state === 'inactive' && !hasRequestedHighlightRef.current) {
			hasRequestedHighlightRef.current = true;
			setIsActive(true);
		}
	}, [setIsActive, state]);

	// Or the first row stays highlighted for the rest of the tour.
	useEffect(() => () => setIsActive(false), [setIsActive]);

	if (state !== 'active') {
		return null;
	}

	return (
		<RemoteCardStep
			{...cardProps}
			body={tourMessages.introBody}
			heading={tourMessages.introHeading}
			isVisible={true}
			offset={[-6, 18]}
			onNext={handleNext}
			placement="bottom-start"
			renderTarget={(target) => <RenderToOpenIssueLink>{target}</RenderToOpenIssueLink>}
		/>
	);
};

type ArrowsStepProps = StepContentProps & {
	surface: ViewSurface;
};

/**
 * "Preview your work items", anchored beside the navigation arrows.
 *
 * Everything mounts inside the anchor, unlike the other two steps: for the modal variant, rendering
 * within the modal's own subtree puts the card above the `@atlaskit/modal-dialog` blanket with no
 * z-index work. The anchor only exists while its surface is open, so this renders nothing until the
 * user opens a work item.
 */
const ArrowsStep: FC<ArrowsStepProps> = ({ surface, ...cardProps }) => {
	const renderInline = useMountToHtmlElement({
		selectors: surface === 'SIDEBAR' ? PANEL_ARROWS_ANCHOR_SELECTOR : MODAL_ARROWS_ANCHOR_SELECTOR,
		as: 'inline',
		position: 'inside',
		// The anchor arrives after the tour mounts, so watch for it rather than querying once.
		observeRoot: getDocument()?.body,
	});

	return renderInline(
		<PopoverProvider>
			<PopoverTarget>
				<div />
			</PopoverTarget>
			<PopoverContent
				dismiss={cardProps.onDismiss}
				isVisible={true}
				placement="left-end"
				shouldDismissOnClickOutside={false}
			>
				<TourCard
					{...cardProps}
					body={surface === 'SIDEBAR' ? tourMessages.arrowsPanelBody : tourMessages.arrowsModalBody}
					heading={tourMessages.arrowsHeading}
					image={{ alt: tourMessages.arrowsImageAlt, src: glanceNavArrowsIllustration }}
				/>
			</PopoverContent>
		</PopoverProvider>,
	);
};

/**
 * Tracks an element's rendered height — no CSS length can express it where the anchor needs it, see
 * `styles.resizeAnchor`. Observed rather than measured on window resize: the panel's height changes
 * for reasons that never reach a `resize` event, such as a banner opening above the content.
 */
const useElementHeight = (element: Element | undefined): number => {
	const [height, setHeight] = useState(0);

	useEffect(() => {
		if (!element) {
			setHeight(0);
			return;
		}

		const measure = () => setHeight(element.getBoundingClientRect().height);

		measure();

		// Absent in jsdom, where the single measurement above is all the tests need.
		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const observer = new ResizeObserver(measure);

		observer.observe(element);

		return () => observer.disconnect();
	}, [element]);

	return height;
};

/**
 * "Expand the panel", anchored to the panel's resize handle.
 *
 * This is the step that gets deferred: a modal has no splitter, so it renders nothing while one is
 * open and appears on its own if a panel opens later in the same session. Nothing is persisted.
 */
const ResizeStep: FC<StepContentProps> = (cardProps) => {
	// The panel can open long after this mounts, so watch the document for the handle appearing.
	const handle = useElement({
		selectors: RESIZE_HANDLE_SELECTOR,
		observeRoot: getDocument()?.body,
	});
	const handleHeight = useElementHeight(handle);

	// Only the target goes inside the handle: its container sets `z-index: 1`, which would trap the
	// card behind the panel's own content.
	const renderToHandle = useInlineNearElement({ targetElement: handle, position: 'inside' });

	// The card is not portalled, so a missing anchor does not hide it: without this it would show
	// unanchored whenever there is no panel, which is the whole of the deferred case.
	if (!handle) {
		return null;
	}

	// False while the handle has no box: it is dropped from the layout under 48rem, and has no box
	// during the frames before the panel finishes rendering. Absent in jsdom — treat as visible.
	const isVisible = handle.checkVisibility?.() ?? true;

	return (
		<RemoteCardStep
			{...cardProps}
			body={tourMessages.resizeBody}
			heading={tourMessages.resizeHeading}
			isVisible={isVisible}
			// No cross-axis shift: the anchor spans the handle, so the card sits against its middle.
			offset={[0, RESIZE_CARD_GAP]}
			placement="left-end"
			renderTarget={renderToHandle}
			targetChild={<Box style={{ height: handleHeight }} xcss={styles.resizeAnchor} />}
		/>
	);
};

export type IssuePreviewPanelChangeboardingTourProps = BaseRenderProps &
	(IssuePreviewPanelSpotlightInlineRenderProps | IssuePreviewPanelSpotlightSidebarRenderProps);

/**
 * The multi-step GA changeboarding tour for the work item preview panel.
 *
 * Step state lives here and each step anchors itself with its own selector. The sequence and its
 * length are derived once and then held: both placement props are reported live, and recomputing
 * would let a visible "n of m" renumber while the user is looking at it.
 */
export const IssuePreviewPanelChangeboardingTour: FC<IssuePreviewPanelChangeboardingTourProps> =
	memo((props) => {
		const { closeWorkItem, openViewMode, preferredViewMode, placementType } = props;

		const [plan] = useState(() =>
			deriveTourPlan({
				openViewMode: openViewMode ?? null,
				// Absent means the GA props were never supplied, so take the shorter sequence: MODAL drops
				// the resize step, which under-promises rather than counting a step that cannot show.
				preferredViewMode: preferredViewMode ?? 'MODAL',
				// The intro step anchors to a table row, which is only reachable from the inline mount.
				canAnchorToList: placementType === 'issue-preview-panel-spotlight-inline',
			}),
		);

		const [stepIndex, setStepIndex] = useState(0);
		const [isDismissed, setIsDismissed] = useState(false);

		const { onClosed, onAcknowledged } = usePostOfficeUserInteractions();

		const total = plan.steps.length;

		// The event shape cannot say "no control was used", so `reason` carries it.
		const reportDismissal = useCallback(
			(reason?: string) =>
				void onClosed({
					interactionIdentifier: 'dismiss',
					...(reason !== undefined && { additionalMetadata: { reason } }),
				}),
			[onClosed],
		);

		const handleDismiss = useCallback(() => {
			setIsDismissed(true);
			reportDismissal();
		}, [reportDismissal]);

		// Reported as the primary route out, separating "read to the end" from a mid-tour dismissal.
		const handleDone = useCallback(() => {
			setIsDismissed(true);
			void onAcknowledged({ interactionIdentifier: 'done' });
		}, [onAcknowledged]);

		const handleNext = useCallback(
			() => setStepIndex((current) => Math.min(current + 1, total - 1)),
			[total],
		);

		/**
		 * Returning to the intro means closing the work item, the mirror of Next opening it: that step
		 * only shows with the list clear. Stepping back anywhere else leaves the surface alone.
		 */
		const handleBack = useCallback(() => {
			if (plan.steps[stepIndex - 1] === 'intro') {
				closeWorkItem?.();
			}

			setStepIndex((current) => Math.max(current - 1, 0));
		}, [closeWorkItem, plan.steps, stepIndex]);

		/**
		 * A work item opening is the intro step's exit condition, however it got there — its own Next,
		 * or the user clicking the row. The step anchors to a list row, so once anything is open it
		 * cannot show, and the arrows step's anchor now exists.
		 *
		 * Edge-triggered on purpose: levelling it off "something is open" would fight Back, which closes
		 * the work item to return here, and that close lands in a later commit than the step change.
		 */
		const isOnIntroStep = plan.steps[stepIndex] === 'intro';
		const wasOpenRef = useRef(Boolean(openViewMode));

		useEffect(() => {
			const isOpen = Boolean(openViewMode);
			const hasJustOpened = isOpen && !wasOpenRef.current;

			wasOpenRef.current = isOpen;

			if (isOnIntroStep && hasJustOpened) {
				setStepIndex((current) => current + 1);
			}
		}, [isOnIntroStep, openViewMode]);

		const arrowsSurface = openViewMode ?? plan.plannedArrowsSurface;
		const arrowsAnchorGeneration = useAnchorGeneration(
			arrowsSurface === 'SIDEBAR' ? PANEL_ARROWS_ANCHOR_SELECTOR : MODAL_ARROWS_ANCHOR_SELECTOR,
		);

		/**
		 * The arrows step is finished once read: minimising or expanding moves the work item to the other
		 * surface, where re-anchoring would present the same card again in different copy. End the tour
		 * instead, reported so the instance is suppressed. Product's call, which is why a panel user who
		 * expands here never reaches the resize step. The resize step itself is left alone.
		 */
		const shownArrowsSurfaceRef = useRef<ViewSurface | null>(null);
		const isOnArrowsStep = plan.steps[stepIndex] === 'arrows';

		useEffect(() => {
			// Stepping off the step ends the comparison: a later visit, reached by Back, is a fresh one
			// rather than a continuation of the surface this one recorded.
			if (!isOnArrowsStep) {
				shownArrowsSurfaceRef.current = null;
				return;
			}

			// Covers both null (nothing open) and absent (the GA props were never supplied).
			if (!openViewMode) {
				return;
			}

			if (shownArrowsSurfaceRef.current === null) {
				shownArrowsSurfaceRef.current = openViewMode;
				return;
			}

			if (shownArrowsSurfaceRef.current !== openViewMode) {
				setIsDismissed(true);
				reportDismissal('surfaceChanged');
			}
		}, [isOnArrowsStep, openViewMode, reportDismissal]);

		// ADS only acts on Escape once focus is inside the card, and we deliberately do not pull focus
		// there for a message the user did not ask for. So Escape needs handling here to work at all.
		const handleDismissRef = useRef(handleDismiss);

		useEffect(() => {
			handleDismissRef.current = handleDismiss;
		}, [handleDismiss]);

		useEffect(() => {
			const handleEscape = (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					handleDismissRef.current();
				}
			};

			getDocument()?.addEventListener('keydown', handleEscape);

			return () => getDocument()?.removeEventListener('keydown', handleEscape);
		}, []);

		if (isDismissed) {
			return null;
		}

		const cardProps: StepContentProps = {
			// Without `closeWorkItem` the intro is unreachable, so Back is not offered at all.
			canGoBack: plan.steps[stepIndex - 1] !== 'intro' || Boolean(closeWorkItem),
			current: stepIndex + 1,
			onBack: handleBack,
			onDismiss: handleDismiss,
			onDone: handleDone,
			onNext: handleNext,
			total,
		};

		// Remounts the step on a surface change: `useElement` caches the node it found for the life of
		// the component, and a panel that closes and reopens is a different element each time.
		const anchorKey = openViewMode ?? 'none';

		switch (plan.steps[stepIndex]) {
			case 'intro':
				// Present in the plan only when the placement mounted inline, so these props are there.
				return placementType === 'issue-preview-panel-spotlight-inline' ? (
					<IntroStep
						{...cardProps}
						openWorkItem={props.openWorkItem}
						setIsActive={props.setIsActive}
						state={props.state}
					/>
				) : null;
			case 'arrows':
				// The surface the work item is showing in, falling back to the planned one while nothing is
				// open. It never follows a surface *change* — that ends the tour above.
				return (
					<ArrowsStep
						{...cardProps}
						key={`${anchorKey}-${arrowsAnchorGeneration}`}
						surface={arrowsSurface}
					/>
				);
			case 'resize':
				return <ResizeStep {...cardProps} key={anchorKey} />;
			default:
				return null;
		}
	});
